import { z } from "zod";
import type { Operation } from "../types";

// Contracts for Settings → Models (docs/models/PRD.md §14). The provider registry,
// per-provider data policy, the single-shot selection, the per-seam model matrix,
// and the copilot dock selection. Every mutation persists the agents.json `models`
// block server-side; a raw key is NEVER sent to or from the client.

export const dataPolicy = z.enum(["zdr", "no-collection", "default"]);
export type DataPolicy = z.infer<typeof dataPolicy>;

export const modelInfoView = z.object({
  id: z.string(),
  label: z.string(),
  contextWindow: z.number(),
  tools: z.string(),
  structuredOutput: z.string(),
});
export type ModelInfoView = z.infer<typeof modelInfoView>;

export const providerView = z.object({
  id: z.string(),
  label: z.string(),
  transport: z.string(),
  baseUrl: z.string().nullable(),
  local: z.boolean(),
  isBuiltin: z.boolean(),
  dataPolicy: dataPolicy,
  /** The env var the key is read from (instruct-env). Never the key itself. */
  keyEnvName: z.string(),
  keyPresent: z.boolean(),
  keySource: z.enum(["env", "sealed", "local", "none"]),
  jurisdiction: z.string().nullable(),
  models: z.array(modelInfoView),
  /** True when this provider is the single-shot selection. */
  singleShot: z.boolean(),
});
export type ProviderView = z.infer<typeof providerView>;

export const modelSeam = z.object({
  key: z.string(),
  label: z.string(),
  group: z.string(),
  /** copilot-develop et al. stay on frontier unless explicitly overridden. */
  frontierDefault: z.boolean(),
});
export type ModelSeam = z.infer<typeof modelSeam>;

export const modelSelection = z.object({ provider: z.string(), model: z.string() });
export type ModelSelection = z.infer<typeof modelSelection>;

export const benchmarkRow = z.object({
  jobClass: z.string(),
  model: z.string(),
  provider: z.string().optional(),
  passRate: z.number().nullable().optional(),
  silentWrong: z.number().nullable().optional(),
  costPerMTok: z.number().nullable().optional(),
  cheapestPassing: z.boolean().optional(),
  when: z.string().nullable().optional(),
  note: z.string().optional(),
});
export type BenchmarkRow = z.infer<typeof benchmarkRow>;

export const benchmarkPayload = z.object({
  source: z.string(),
  generatedAt: z.string().nullable(),
  rows: z.array(benchmarkRow),
});
export type BenchmarkPayload = z.infer<typeof benchmarkPayload>;

const configResponse = z.object({
  providers: z.array(providerView),
  seams: z.array(modelSeam),
  matrix: z.record(z.string(), modelSelection),
  singleShot: modelSelection.nullable(),
  copilot: modelSelection.nullable(),
  copilotBranchEnabled: z.boolean(),
  benchmark: benchmarkPayload.nullable(),
});
export type ModelsConfigResponse = z.infer<typeof configResponse>;

const get: Operation<void, void, void, ModelsConfigResponse> = {
  method: "GET",
  path: () => "/api/models/config",
  response: configResponse,
};

const saveProviderBody = z.object({
  id: z.string().min(1).max(64),
  transport: z.string().max(32).optional(),
  baseUrl: z.string().max(512).optional(),
  dataPolicy: dataPolicy.optional(),
  jurisdiction: z.string().max(400).optional(),
  /** Make this the single-shot provider, with this model. */
  setSingleShot: z.boolean().optional(),
  model: z.string().max(200).optional(),
});
export type SaveProviderBody = z.infer<typeof saveProviderBody>;

const saveProviderResponse = z.object({
  ok: z.boolean(),
  provider: providerView,
  keyEnvName: z.string(),
  keyPresent: z.boolean(),
});

const saveProvider: Operation<void, void, SaveProviderBody, z.infer<typeof saveProviderResponse>> = {
  method: "POST",
  path: () => "/api/models/config/provider",
  body: saveProviderBody,
  response: saveProviderResponse,
  invalidates: [get],
};

const setDataPolicyBody = z.object({ id: z.string().min(1), dataPolicy });
const setDataPolicy: Operation<void, void, z.infer<typeof setDataPolicyBody>, { ok: boolean }> = {
  method: "POST",
  path: () => "/api/models/config/data-policy",
  body: setDataPolicyBody,
  response: z.object({ ok: z.boolean() }),
  invalidates: [get],
};

const setSingleShotBody = z.object({
  provider: z.string(),
  model: z.string(),
});
const setSingleShot: Operation<void, void, z.infer<typeof setSingleShotBody>, { ok: boolean }> = {
  method: "POST",
  path: () => "/api/models/config/single-shot",
  body: setSingleShotBody,
  response: z.object({ ok: z.boolean() }),
  invalidates: [get],
};

const saveMatrixBody = z.object({
  matrix: z.record(z.string(), modelSelection.nullable()),
});
const saveMatrix: Operation<void, void, z.infer<typeof saveMatrixBody>, { ok: boolean }> = {
  method: "POST",
  path: () => "/api/models/config/matrix",
  body: saveMatrixBody,
  response: z.object({ ok: z.boolean() }),
  invalidates: [get],
};

const setCopilotBody = z.object({
  /** Absent ⇒ leave the selection unchanged; null ⇒ clear it. */
  provider: z.string().nullable().optional(),
  model: z.string().optional(),
  /** The `agents.copilot.provider` master switch — off by default. */
  enableBranch: z.boolean().optional(),
});
const setCopilotResponse = z.object({
  ok: z.boolean(),
  copilot: modelSelection.nullable(),
  branchEnabled: z.boolean(),
});
const setCopilot: Operation<void, void, z.infer<typeof setCopilotBody>, z.infer<typeof setCopilotResponse>> = {
  method: "POST",
  path: () => "/api/models/config/copilot",
  body: setCopilotBody,
  response: setCopilotResponse,
  invalidates: [get],
};

const setKeyBody = z.object({
  id: z.string().min(1),
  /** The pasted key. Sealed server-side (AES-256-GCM, integration_credentials);
   * never echoed back, never written to agents.json. */
  key: z.string().min(8),
  /** Also make this provider the single-shot selection (onboarding path). */
  setSingleShot: z.boolean().optional(),
  model: z.string().optional(),
});
const setKeyResponse = z.object({
  ok: z.boolean(),
  keyPresent: z.boolean(),
  keySource: z.enum(["env", "sealed", "local", "none"]),
});
const setKey: Operation<void, void, z.infer<typeof setKeyBody>, z.infer<typeof setKeyResponse>> = {
  method: "POST",
  path: () => "/api/models/config/key",
  body: setKeyBody,
  response: setKeyResponse,
  invalidates: [get],
};
export type SetProviderKeyBody = z.infer<typeof setKeyBody>;

export const modelsConfig = {
  get,
  saveProvider,
  setDataPolicy,
  setSingleShot,
  saveMatrix,
  setCopilot,
  setKey,
};
